import { UserField } from '@dadohr/shared/domainTypes/experience';
import { Rule } from '@dadohr/shared/domainTypes/rules';
import { useTheme } from '@emotion/react';
import { Button, Menu, MenuItem, Tooltip, Typography } from '@material-ui/core';
import {
  ChevronDown,
  Download,
  Pencil,
  PlusCircle,
  RotateCcw,
  Share2,
  Trash2
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Redirect } from 'react-router';
import { ButtonWithPromise } from '../../../../components/ButtonWithPromise';
import { ColumnSelector } from '../../../../components/ColumnSelector';
import { Loader } from '../../../../components/Loader';
import { NoAccess } from '../../../../components/NoAccess';
import { SaveViewModal } from '../../../../components/SaveViewModal';
import { ShareViewModal } from '../../../../components/ShareViewModal';
import { TableSearch } from '../../../../components/Table';
import { UserFilterDialog } from '../../../../components/UserFilterDialog';
import { UserTable } from '../../../../components/UserTable';
import {
  DETAILED_NAME_COLUMNS,
  useUserTableColumnsAndSorters
} from '../../../../components/UserTable/columns';
import { useExportUsersCsv } from '../../../../components/UserTable/useExportUsersCsv';
import { useDialogState } from '../../../../hooks/useDialogState';
import { useRouter } from '../../../../hooks/useRouter';
import { useUnsafeTranslation } from '../../../../i18next';
import { FlexContainer } from '../../../../layout/Flex';
import { PageBody } from '../../../../layout/PageBody';
import {
  PageToolbar,
  PageToolbarActions,
  PageToolbarTitle
} from '../../../../layout/PageToolbar';
import { useRoutes, useStringQueryParam } from '../../../../routes';
import { useAdminUserById } from '../../../../services/adminUsers';
import { useCurrentUser } from '../../../../services/currentUser';
import { hasPermissionToAccessPage } from '../../../../services/permissions';
import {
  createUserSavedView,
  updateUserSavedView,
  useUserSavedViewById
} from '../../../../services/savedView';
import { now } from '../../../../services/time';
import {
  matchUser,
  matchUserBySearchTerm,
  toFullName,
  toSearchTermRegexp,
  useUsersByOrgId
} from '../../../../services/user';
import { isEmptyRule } from '../../../Experiences/components/RuleBuilder';
import { useUsersSideNav } from '../sideNav';
import { DeleteViewModal } from './components/DeleteViewModal';
import { RenameViewModal } from './components/RenameViewModal';

const SaveViewDropdown = ({
  onSave,
  onSaveAsNew
}: {
  onSave: () => Promise<void>;
  onSaveAsNew: () => void;
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setAnchorEl(null);
    setSaving(true);
    try {
      await onSave();
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        color="primary"
        endIcon={<ChevronDown size={16} />}
        disabled={saving}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        {saving ? 'Saving...' : 'Save view'}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem onClick={handleSave}>
          <RotateCcw size={16} style={{ marginRight: 8 }} />
          Update existing saved view
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            onSaveAsNew();
          }}
        >
          <PlusCircle size={16} style={{ marginRight: 8 }} />
          Create new saved view
        </MenuItem>
      </Menu>
    </>
  );
};

const ManageViewDropdown = ({
  isCreator,
  onShare,
  onRename,
  onDelete
}: {
  isCreator: boolean;
  onShare: () => void;
  onRename: () => void;
  onDelete: () => void;
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const close = () => setAnchorEl(null);
  const runAndClose = (fn: () => void) => () => {
    close();
    fn();
  };

  return (
    <>
      <Button
        variant="outlined"
        color="primary"
        endIcon={<ChevronDown size={16} />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        Manage view
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={close}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {isCreator && (
          <MenuItem onClick={runAndClose(onShare)}>
            <Share2 size={16} style={{ marginRight: 8 }} />
            Share view
          </MenuItem>
        )}
        {isCreator && (
          <MenuItem onClick={runAndClose(onRename)}>
            <Pencil size={16} style={{ marginRight: 8 }} />
            Rename view
          </MenuItem>
        )}
        <MenuItem
          onClick={runAndClose(onDelete)}
          style={{ color: theme.custom.colors.red.w500 }}
        >
          <Trash2 size={16} style={{ marginRight: 8 }} />
          Delete view
        </MenuItem>
      </Menu>
    </>
  );
};

const SavedViewContent = ({ viewId }: { viewId: string }) => {
  const { t } = useUnsafeTranslation();
  const currentUser = useCurrentUser();
  const { org } = currentUser;
  const sideNav = useUsersSideNav();
  const [users, loading] = useUsersByOrgId(org.id);
  const [savedView, viewLoading] = useUserSavedViewById(viewId);
  const { ROUTES } = useRoutes();
  const { history } = useRouter();
  const [q, setQ] = useStringQueryParam('q', '');
  const filterDialog = useDialogState();
  const saveAsNewModal = useDialogState();
  const shareModal = useDialogState();
  const renameModal = useDialogState();
  const deleteModal = useDialogState();

  const isCreator = savedView?.data.createdByUserId === currentUser.id;
  const [creator] = useAdminUserById(
    org.id,
    savedView?.data.createdByUserId ?? ''
  );

  const { columns: allColumns, sorters } = useUserTableColumnsAndSorters(
    org.customUserFields
  );
  const columns = useMemo(
    () => allColumns.filter((c) => !DETAILED_NAME_COLUMNS.includes(c.key)),
    [allColumns]
  );

  const [filters, setFilters] = useState<Rule<UserField> | null>(null);
  const [selectedColumns, setSelectedColumns] = useState(columns);
  const [sortKey, setSortKey] = useState('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const initialized = useRef(false);
  const [lastSaved, setLastSaved] = useState<{ columns: string; filters: string; sortKey: string; sortDirection: string } | null>(null);

  useEffect(() => {
    if (!savedView || initialized.current) return;
    initialized.current = true;

    setFilters(savedView.data.filters);
    setSortKey(savedView.data.sortKey);
    setSortDirection(savedView.data.sortDirection);

    const columnsByKey = new Map(columns.map((c) => [c.key, c]));
    const restoredColumns = savedView.data.columns
      .map((key) => columnsByKey.get(key))
      .filter((c): c is (typeof columns)[number] => c !== undefined);
    const restored = restoredColumns.length ? restoredColumns : columns;
    setSelectedColumns(restored);

    setLastSaved({
      columns: restored.map((c) => c.key).join(','),
      filters: JSON.stringify(savedView.data.filters),
      sortKey: savedView.data.sortKey,
      sortDirection: savedView.data.sortDirection
    });
  }, [savedView, columns]);

  const hasUnsavedChanges = useMemo(() => {
    if (!lastSaved) return false;
    return (
      JSON.stringify(filters) !== lastSaved.filters ||
      selectedColumns.map((c) => c.key).join(',') !== lastSaved.columns ||
      sortKey !== lastSaved.sortKey ||
      sortDirection !== lastSaved.sortDirection
    );
  }, [lastSaved, filters, selectedColumns, sortKey, sortDirection]);

  const hasFiltersApplied = filters && !isEmptyRule(filters);

  const selectedUsers = useMemo(() => {
    if (!users) return null;
    let filteredUsers = users;
    if (q) {
      const regexp = toSearchTermRegexp(q);
      filteredUsers = filteredUsers.filter((u) =>
        matchUserBySearchTerm(regexp, u)
      );
    }
    if (filters && !isEmptyRule(filters)) {
      filteredUsers = filteredUsers.filter((u) => matchUser(u, filters, now()));
    }
    return filteredUsers;
  }, [q, users, filters]);

  const { exportCsv, canExport } = useExportUsersCsv({
    users,
    selectedUsers,
    selectedColumns,
    customUserFields: org.customUserFields,
    dateFormat: org.dateFormats.MMDDYYYY
  });

  const handleSave = async () => {
    const columnKeys = selectedColumns.map((c) => c.key);
    await updateUserSavedView({
      viewId,
      filters,
      columns: columnKeys,
      sortKey,
      sortDirection
    });
    setLastSaved({
      columns: columnKeys.join(','),
      filters: JSON.stringify(filters),
      sortKey,
      sortDirection
    });
  };

  const handleSaveAsNew = async (name: string) => {
    const result = await createUserSavedView({
      orgId: org.id,
      name,
      filters,
      columns: selectedColumns.map((c) => c.key),
      sortKey,
      sortDirection
    });
    history.push(ROUTES.users.views.detail.url(result.id), { newViewId: result.id });
  };

  const handleLeaveView = () => {
    history.push(ROUTES.users.overview.url());
  };

  if (viewLoading) return <Loader height={500} />;

  if (!savedView) return <Redirect to={ROUTES.users.overview.url()} />;

  if (savedView.data.orgId !== org.id) return <Redirect to={ROUTES.users.overview.url()} />;

  return (
    <PageBody noTopPadding sideNav={sideNav}>
      <PageToolbar sticky>
        <PageToolbarTitle>
          <Typography component="h1" variant="h1">
            {savedView?.data.name ?? t('pageTitle.orgUsersOverview')}
          </Typography>
        </PageToolbarTitle>
        <PageToolbarActions>
          {!isCreator && (
            <>
              <Tooltip
                title={`Only the creator of this view (${toFullName(
                  creator?.data ?? { preferredName: '', lastName: '' }
                )}) can share it or make changes. You can save a copy, which you'll be able to edit and share.`}
                placement="bottom"
              >
                <span>
                  <Button startIcon={<Share2 size={16} />} disabled>
                    Shared by{' '}
                    {creator?.data.preferredName ||
                      creator?.data.firstName ||
                      '...'}
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="outlined"
                color="primary"
                onClick={saveAsNewModal.openDialog}
              >
                Save a copy
              </Button>
            </>
          )}
          {isCreator && hasUnsavedChanges && (
            <SaveViewDropdown
              onSave={handleSave}
              onSaveAsNew={saveAsNewModal.openDialog}
            />
          )}
          <ManageViewDropdown
            isCreator={isCreator}
            onShare={shareModal.openDialog}
            onRename={renameModal.openDialog}
            onDelete={deleteModal.openDialog}
          />
          <Button
            color="primary"
            variant={hasFiltersApplied ? 'contained' : 'outlined'}
            onClick={() => filterDialog.openDialog()}
          >
            {hasFiltersApplied ? t('Edit filters') : t('Add filters')}
          </Button>
          <ColumnSelector
            value={selectedColumns}
            onChange={setSelectedColumns}
            columns={columns}
            requiredColumns={new Set(['name'])}
            withAllOption
            withSearch
          />
          {canExport && (
            <ButtonWithPromise
              startIcon={<Download size={16} />}
              variant="outlined"
              pending={`${t('Exporting')}...`}
              onClick={exportCsv}
            >
              {t('Export')}
            </ButtonWithPromise>
          )}
          <TableSearch
            term={q}
            onChange={setQ}
            autoFocus
            placeholder={t('users.overview.search')}
          />
        </PageToolbarActions>
      </PageToolbar>

      {loading && <Loader height={500} />}
      {users && selectedUsers && (
        <>
          <FlexContainer justifyContent="flex-end">
            <Typography variant="body1" color="textSecondary">
              {users.length === 1
                ? t(`Showing ${selectedUsers.length} of ${users.length} user`)
                : t(`Showing ${selectedUsers.length} of ${users.length} users`)}
            </Typography>
          </FlexContainer>

          <UserTable
            ds={selectedUsers}
            columns={selectedColumns}
            sorters={sorters}
            variant="contained"
            rowToHref={(d) => ROUTES.users.profile.url(d.id)}
            headProps={{ sticky: true, offset: 124 }}
            dateFormat={org.dateFormats.MMDDYYYY}
            sortKey={sortKey}
            sortDirection={sortDirection}
            onSortChange={(k, dir) => {
              setSortKey(k);
              setSortDirection(dir);
            }}
            onColumnReorder={setSelectedColumns}
            horizontalScroll
          />
        </>
      )}

      {filterDialog.dialogOpen && (
        <UserFilterDialog
          open={true}
          onClose={filterDialog.closeDialog}
          value={filters}
          onChange={setFilters}
          orgId={org.id}
          customFields={org.customUserFields}
          title="User Filters"
        />
      )}

      {saveAsNewModal.dialogOpen && (
        <SaveViewModal
          open={true}
          onClose={saveAsNewModal.closeDialog}
          onSave={handleSaveAsNew}
        />
      )}

      {shareModal.dialogOpen && savedView && (
        <ShareViewModal
          open={true}
          onClose={shareModal.closeDialog}
          viewId={viewId}
          creatorId={savedView.data.createdByUserId}
          alreadySharedWith={savedView.data.sharedWith ?? []}
        />
      )}

      {renameModal.dialogOpen && savedView && (
        <RenameViewModal
          open={true}
          onClose={renameModal.closeDialog}
          viewId={viewId}
          currentName={savedView.data.name}
        />
      )}

      {deleteModal.dialogOpen && savedView && (
        <DeleteViewModal
          open={true}
          onClose={deleteModal.closeDialog}
          viewId={viewId}
          viewName={savedView.data.name}
          isCreator={isCreator}
          sharedWithCount={(savedView.data.sharedWith ?? []).length}
          creatorName={toFullName(
            creator?.data ?? { preferredName: '', lastName: '' }
          )}
          onConfirmed={handleLeaveView}
          onStale={handleLeaveView}
        />
      )}
    </PageBody>
  );
};

export const PageUserSavedView = ({ viewId }: { viewId: string }) => {
  const currentUser = useCurrentUser();
  const { ROUTES } = useRoutes();
  if (!hasPermissionToAccessPage(currentUser, ROUTES.users.overview)) {
    return <NoAccess />;
  }

  return <SavedViewContent viewId={viewId} />;
};
